import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { scanNow, scheduleDrawerQuery, schedulesQuery, type ScheduleRow } from '../../api/admRuns'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField } from './AdmFilterBar'
import { AdmViews } from './AdmViews'
import { STATE_PILL, esc } from './AdmRunBits'
import { CloudCollectionPanel } from './CloudCollectionPanel'

/**
 * ADM -> Discovery schedule, din `views/version-3/softsee/adm/schedules/index.php`.
 *
 * CITESTE DIN DOUA TABELE si nu detine niciunul. `it_discovery_ranges` e
 * intinderea noua - mai multe intervale pe releu, cu excluderi. Cat de des se
 * scaneaza sta mai departe in `it_discovery_settings`, tot al ecranului de
 * setari si tot ce urmeaza agentul; copiat aici, platforma ar fi avut doua
 * raspunsuri la "cand ruleaza asta".
 *
 * "URMATOAREA RULARE" E SOCOTITA, nu tinuta minte. Una scrisa in baza trebuie
 * pastrata corecta de cine o scrie, si se strica tacut cand nu se intampla asta.
 *
 * ECRANUL ASTA RAPORTEAZA; nu hotaraste. Intervalele si recurenta se scriu pe
 * ecranul de setari al descoperirii, si legatura catre el e chiar deosebirea
 * dintre o pagina pe care cineva o citeste si una pe care poate lucra.
 */

const STYLES = ['css/adm.css']

/** Sertarul unui interval: se cere abia cand randul chiar se deschide. */
function ScheduleDrawer({ id }: { id: number }) {
  const drawer = useQuery(scheduleDrawerQuery(id))

  if (drawer.isPending) {
    return (
      <div className="adm-expand adm-muted">
        <i className="fas fa-circle-notch fa-spin"></i> Loading…
      </div>
    )
  }

  if (drawer.isError || !drawer.data) {
    return <div className="adm-expand adm-muted">This range could not be loaded.</div>
  }

  const range = drawer.data.range

  return (
    <div className="adm-expand">
      <div className="row">
        <div className="col-md-6">
          <h5 className="adm-expand-head">
            <i className="fas fa-info-circle"></i> The range
          </h5>

          <table className="table table-condensed adm-list">
            <tbody>
              <tr>
                <td style={{ width: 150 }} className="text-muted">
                  Kind
                </td>
                <td>
                  {range.excluded ? (
                    <>
                      <span className="adm-pill adm-pill-warn">Excluded</span>{' '}
                      <span className="adm-muted" style={{ fontSize: 11 }}>
                        never scanned, even when another range covers it
                      </span>
                    </>
                  ) : (
                    <span className="adm-pill adm-pill-ok">Scanned</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="text-muted">Addresses</td>
                <td className="adm-nowrap">
                  {range.ipFrom} – {range.ipTo}
                </td>
              </tr>
              <tr>
                <td className="text-muted">Customer</td>
                <td>{range.companyName}</td>
              </tr>
              <tr>
                <td className="text-muted">Relay</td>
                <td>
                  {range.relayName || '—'}
                  {range.relayLastSeen ? (
                    <div className="adm-muted" style={{ fontSize: 11 }}>
                      last heard from {range.relayLastSeen}
                    </div>
                  ) : null}
                </td>
              </tr>
              <tr>
                <td className="text-muted">Recurrence</td>
                <td>{range.recurrenceLabel}</td>
              </tr>
              <tr>
                <td className="text-muted">Active</td>
                <td>{range.active ? <span className="adm-pill adm-pill-ok">Yes</span> : <span className="adm-pill">No</span>}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-md-6">
          <h5 className="adm-expand-head">
            <i className="fas fa-history"></i> The last run
          </h5>

          {range.lastRunAt === '' ? (
            <div className="adm-empty-inline">
              <i className="fas fa-calendar-alt"></i> This range has never run.
              {range.relayName === '' ? ' It has no relay, which is why.' : ''}
            </div>
          ) : (
            <table className="table table-condensed adm-list">
              <tbody>
                <tr>
                  <td style={{ width: 150 }} className="text-muted">
                    Started
                  </td>
                  <td className="adm-nowrap">
                    <a href={`/app/adm/run/${range.lastRunId}`}>{range.lastRunAt}</a>{' '}
                    <span className={`adm-pill ${STATE_PILL[range.lastRunState] ?? ''}`}>{range.lastRunStateLabel}</span>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Answered</td>
                  <td>
                    {range.targetsResponded} of {range.targetsTotal}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">New devices</td>
                  <td>{range.ciCreated}</td>
                </tr>
                <tr>
                  <td className="text-muted">Failures</td>
                  <td className={range.errorsCount > 0 ? 'text-danger' : 'adm-muted'}>{range.errorsCount}</td>
                </tr>
              </tbody>
            </table>
          )}

          <p className="text-muted small" style={{ marginBottom: 0 }}>
            Next run {range.nextRunAt === '' ? 'is not scheduled' : range.nextRunAt}.
          </p>
        </div>
      </div>
    </div>
  )
}

/** Coloanele listei de intervale, cu markupul din macheta. */
function rangeColumns(canWrite: boolean, asked: number[]): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      className: 'text-center',
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Range',
      orderable: true,
      visible: true,
      html: (row) => {
        const r = row as unknown as ScheduleRow
        /* O excludere nu e o includere mai mica. Aratate la fel, cineva ajunge
           sa se intrebe de ce o subretea nu se scaneaza niciodata. */
        const pill = r.excluded ? ' <span class="adm-pill adm-pill-warn">Excluded</span>' : ''
        return esc(r.name || 'Unnamed range') + pill
      },
    },
    { index: 2, title: 'Customer', orderable: true, visible: true, className: 'adm-muted', html: (row) => esc(row.companyName) },
    { index: 3, title: 'Relay', orderable: true, visible: true, className: 'adm-muted', html: (row) => esc(row.relayName) },
    {
      index: 4,
      title: 'Addresses',
      orderable: false,
      visible: true,
      className: 'adm-nowrap',
      html: (row) => {
        const r = row as unknown as ScheduleRow
        return `${esc(r.ipFrom)} &ndash; ${esc(r.ipTo)}`
      },
    },
    { index: 5, title: 'Recurrence', orderable: true, visible: true, className: 'adm-muted', html: (row) => esc(row.recurrenceLabel) },
    {
      index: 6,
      title: 'Last run',
      orderable: true,
      visible: true,
      className: 'adm-nowrap',
      html: (row) => {
        const r = row as unknown as ScheduleRow
        if (r.lastRunAt === '') return '<span class="adm-muted">never</span>'
        return (
          `<a href="/app/adm/run/${r.lastRunId}">${esc(r.lastRunAt)}</a> ` +
          `<span class="adm-pill ${STATE_PILL[r.lastRunState] ?? ''}">${esc(r.lastRunStateLabel)}</span>`
        )
      },
    },
    {
      index: 7,
      title: 'Next run',
      orderable: true,
      visible: true,
      className: 'adm-nowrap adm-muted',
      html: (row) => esc((row as unknown as ScheduleRow).nextRunAt || '—'),
    },
    {
      index: 8,
      title: 'Active',
      orderable: true,
      visible: true,
      html: (row) =>
        (row as unknown as ScheduleRow).active ? '<span class="adm-pill adm-pill-ok">Yes</span>' : '<span class="adm-pill">No</span>',
    },
    {
      index: 9,
      title: '',
      orderable: false,
      visible: true,
      width: 110,
      className: 'text-right adm-nowrap',
      html: (row) => {
        const r = row as unknown as ScheduleRow
        if (!canWrite || !r.relayDeviceId) return ''
        /*
         * Un releu are de obicei mai multe intervale, deci un rand - si un
         * buton - pe interval. Cererea e PE RELEU si le acopera pe toate, deci
         * dupa ce s-a cerut o data, toate butoanele lui se sting: lasate vii ar
         * fi invitat o a doua apasare care nu poate decat sa fie refuzata.
         */
        const already = asked.includes(r.relayDeviceId)
        return (
          `<button type="button" class="btn btn-danger btn-xs admScanNow" data-relay="${r.relayDeviceId}"` +
          `${already ? ' disabled' : ''} title="Ask this relay to scan now">` +
          `<i class="fas fa-play"></i> Scan now</button>`
        )
      },
    },
  ]
}

/** Sortarea locala a listei: intervalele vin toate deodata, nu paginate de server. */
function sortRanges(rows: ScheduleRow[], column: number, dir: 'asc' | 'desc'): ScheduleRow[] {
  const keys: Record<number, keyof ScheduleRow> = {
    1: 'name',
    2: 'companyName',
    3: 'relayName',
    5: 'recurrenceLabel',
    6: 'lastRunAt',
    7: 'nextRunAt',
    8: 'active',
  }
  const key = keys[column]
  if (!key) return rows

  const sorted = [...rows].sort((a, b) => String(a[key] ?? '').localeCompare(String(b[key] ?? '')))
  return dir === 'asc' ? sorted : sorted.reverse()
}

export function SchedulesPage() {
  const shellData = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shellData.data?.version ?? '')

  const [relay, setRelay] = useState('')
  const [asked, setAsked] = useState<number[]>([])

  const page = useQuery(schedulesQuery(relay))
  /* Coloana zero e desfasuratorul, si el nu sorteaza: asa lista ramane in
     ordinea SERVERULUI - client, apoi nume - pana cand cineva apasa un cap de
     coloana. Pornita pe nume, ea ar fi contrazis din prima clipa ordinea in
     care portalul a asezat mereu intervalele. */
  const [state, setState] = useTableState('admSchedules', {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'asc' },
    search: '',
  })

  const canWrite = page.data?.canWrite ?? false
  const columns = useMemo(() => rangeColumns(canWrite, asked), [canWrite, asked])

  const all = useMemo(() => sortRanges(page.data?.ranges ?? [], state.order.column, state.order.dir), [page.data, state.order])
  const shown = useMemo(() => all.slice(state.start, state.start + state.length), [all, state.start, state.length])

  const ask = async (relayId: number) => {
    setAsked((current) => (current.includes(relayId) ? current : [...current, relayId]))
    try {
      const answer = await scanNow(relayId)
      /* Un releu care nu-si mai ia cererile nu e o reusita: cererea sta in
         coada, dar n-o porneste nimeni. Galben, cu motivul de la server. */
      toast(answer.message, 'Discovery schedule', answer.status !== 'OK' ? 'error' : answer.stalled ? 'warning' : 'success')
      /* Lasat stins la reusita: releul are cererea, iar o a doua apasare n-ar
         adauga decat una de refuzat. */
      if (answer.status !== 'OK') setAsked((current) => current.filter((id) => id !== relayId))
    } catch (error) {
      setAsked((current) => current.filter((id) => id !== relayId))
      toast((error as Error).message, 'Discovery schedule', 'error')
    }
  }

  const counts = page.data?.counts ?? { total: 0, excluded: 0, neverRun: 0 }

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="green-mode"
          icon="fa-calendar-alt"
          title="Discovery schedule"
          note="which addresses are scanned, how often, and when next. Next run is derived, never stored"
          stats={[
            { value: counts.total.toLocaleString('en-US'), label: 'ranges', tone: 'green-mode' },
            { value: counts.excluded.toLocaleString('en-US'), label: 'excluded', tone: counts.excluded > 0 ? 'yellow' : 'grey' },
            { value: counts.neverRun.toLocaleString('en-US'), label: 'never run', tone: counts.neverRun > 0 ? 'red-intense' : 'grey' },
          ]}
          actions={
            canWrite ? (
              /* Ecranul asta raporteaza; scrisul e pe setarile descoperirii, si
                 legatura catre ele e chiar deosebirea dintre o pagina pe care
                 cineva o citeste si una pe care poate lucra. */
              <a className="btn btn-default btn-sm" href="/app/settings/softsee#scan_rules">
                <i className="fas fa-sliders-h"></i> All scan settings
              </a>
            ) : undefined
          }
        />

        <AdmViews views={page.data?.views ?? []} current="schedules" />

        <AdmFilterBar>
          <AdmField label="Relay" htmlFor="admRelay" wide>
            <select className="form-control input-sm" id="admRelay" value={relay} onChange={(e) => setRelay(e.target.value)}>
              <option value="">Any relay</option>
              {(page.data?.relays ?? []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </AdmField>
        </AdmFilterBar>

        <div id="admSchedulesTable" onClick={(event) => {
          const target = (event.target as HTMLElement).closest('.admScanNow')
          if (!target) return
          void ask(Number(target.getAttribute('data-relay')))
        }}>
          <DataTable
            id="admSchedules"
            columns={columns}
            rows={shown as unknown as Record<string, unknown>[]}
            total={all.length}
            fetching={page.isFetching}
            error={page.isError ? (page.error as Error).message : null}
            onRetry={() => void page.refetch()}
            state={state}
            onState={setState}
            rowId={(r) => String(r.DT_RowId ?? r.ID)}
            details={(row) => <ScheduleDrawer id={Number(row.ID)} />}
            footerButtons={{ copy: true, colvis: true, refresh: () => void page.refetch() }}
            emptyHint="No discovery ranges are configured yet."
          />
        </div>

        <div className="adm-muted" style={{ fontSize: 12, marginTop: 10 }}>
          Ranges and recurrence are edited on the relay&apos;s discovery settings. This screen shows what is configured across
          every relay at once.
          <br />
          {/* Spus pe fata, fiindca un buton care pare instantaneu si nu e
              produce o a doua apasare, si apoi o a treia. */}
          <strong>Scan now</strong> asks the relay to start; it picks the request up within a minute, sooner if it is connected.
          It uses the ranges and the settings it already has — the request carries nothing else.
        </div>

        {/* Colectarea AWS / Azure nu are interval si nici releu: orele ei sunt
            pe firma, si se scriu chiar aici (cerinta 87). */}
        <CloudCollectionPanel />
      </div>
    </Content>
  )
}
